import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SnackbarService } from 'src/app/infrastructure/service/snackbar.service';
import { ConfirmationDialogData } from 'src/app/app-layout/components/others/confirmation-dialog/confirmation-dialog-data';
import { ConfirmationDialogService } from 'src/app/app-layout/components/others/confirmation-dialog/confirmation-dialog.service';
import { AuthService } from 'src/app/infrastructure/auth/auth.service';
import {
  BillingCheckoutRequest,
  BillingLimitUsage,
  BillingMySubscriptionSummary,
  BillingPlanCatalogItem,
  BillingSubscriptionPeriod,
} from '../../modules/user-tenant-settings/domain/my-subscription';
import { PlanBillingService } from './plan-billing.service';

@Component({
    selector: 'app-plan',
    templateUrl: './plan.component.html',
    styleUrls: ['./plan.component.scss'],
    standalone: false
})
export class PlanComponent implements OnInit {

  loading = true;
  loadError = false;
  loadingPlan: string | null = null;
  couponApplying = false;
  couponCode = '';
  billingPeriod: BillingSubscriptionPeriod = 'MONTHLY';
  catalog: BillingPlanCatalogItem[] = [];
  mySubscription: BillingMySubscriptionSummary | null = null;
  comparisonServices: string[] = [];
  fixedComparisonItems: Array<{ key: string; label: string; serviceKey?: string }> = [
    { key: 'agenda_tasks', label: 'Controle de agendas e tarefas' },
    { key: 'project_management', label: 'Gestão de projetos' },
    { key: 'process_control', label: 'Controle de processos' },
    { key: 'finance_management', label: 'Gestão Financeira' }
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly mySubscriptionBillingService: PlanBillingService,
    private readonly snackbarService: SnackbarService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly authService: AuthService
  ) { }

  async ngOnInit(): Promise<void> {
    this.handleCheckoutStatus();
    this.couponCode = this.couponCode || this.authService.getCouponCode() || '';
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.loading = true;
    this.loadError = false;

    try {
      const [catalog, mySubscription] = await Promise.all([
        this.mySubscriptionBillingService.getCatalog(this.couponCode || undefined),
        this.loadMySubscription()
      ]);
      this.catalog = this.ensureCurrentPlanInCatalog(catalog || [], mySubscription);
      this.mySubscription = mySubscription;
      this.couponCode = this.catalog.find(plan => !!plan.couponCode)?.couponCode || this.couponCode;
      this.billingPeriod = mySubscription?.billingPeriod || 'MONTHLY';
      this.comparisonServices = this.buildComparisonServices(this.catalog, mySubscription);
    } catch (error) {
      this.loadError = true;
      this.snackbarService.error('Não foi possível carregar os planos. Tente novamente.');
    } finally {
      this.loading = false;
    }
  }

  async applyCoupon(): Promise<void> {
    if (this.couponApplying || this.loadingPlan) {
      return;
    }
    this.couponApplying = true;
    try {
      const catalog = await this.mySubscriptionBillingService.getCatalog(this.couponCode || undefined);
      this.catalog = this.ensureCurrentPlanInCatalog(catalog || [], this.mySubscription);
      this.couponCode = this.catalog.find(plan => !!plan.couponCode)?.couponCode || this.couponCode;
      this.comparisonServices = this.buildComparisonServices(this.catalog, this.mySubscription);
      this.snackbarService.success(this.couponCode ? 'Cupom aplicado.' : 'Preços atualizados.');
    } catch (_error) {
      this.snackbarService.error('Cupom inválido ou indisponível.');
    } finally {
      this.couponApplying = false;
    }
  }

  async startCheckout(plan: BillingPlanCatalogItem): Promise<void> {
    if (this.loadingPlan) {
      return;
    }

    if (!plan?.planId) {
      this.snackbarService.error('Plano inválido para checkout.');
      return;
    }

    this.loadingPlan = plan.planId;

    try {
      if (this.isDowngrade(plan)) {
        const response = await this.mySubscriptionBillingService.scheduleDowngrade({ planId: plan.planId, couponCode: this.couponCode || undefined });
        if (this.mySubscription) {
          this.mySubscription = {
            ...this.mySubscription,
            pendingPlanName: response?.planName || plan.name,
            pendingPlanChangeType: 'DOWNGRADE',
            pendingPlanChangeStatus: response?.status || 'PENDING'
          };
        }
        this.snackbarService.success(this.getDowngradeScheduledMessage(response?.effectiveAt));
        return;
      }

      if (this.mySubscription?.planName && !this.isInitialTrial() && !this.isCurrentFreePlan()) {
        const response = await this.mySubscriptionBillingService.upgrade({
          planId: plan.planId,
          billingPeriod: this.billingPeriod,
          couponCode: this.couponCode || undefined,
          successUrl: this.buildReturnUrl('success'),
          pendingUrl: this.buildReturnUrl('pending'),
          failureUrl: this.buildReturnUrl('error')
        });
        const checkoutUrl = response?.authorizationRequired
          ? response.authorizationCheckoutUrl
          : response?.checkoutUrl;
        if (!checkoutUrl) {
          this.snackbarService.info('Checkout em preparação. Aguarde alguns instantes e tente novamente.');
          return;
        }
        window.location.assign(checkoutUrl);
        return;
      }

      const request: BillingCheckoutRequest = {
        planId: plan.planId,
        billingPeriod: this.billingPeriod,
        successUrl: this.buildReturnUrl('success'),
        pendingUrl: this.buildReturnUrl('pending'),
        failureUrl: this.buildReturnUrl('error'),
        couponCode: this.couponCode || undefined
      };

      const response = await this.mySubscriptionBillingService.checkout(request);
      if (!response?.initPoint) {
        this.snackbarService.error('Checkout indisponível no momento.');
        return;
      }

      window.location.assign(response.initPoint);
    } catch (error) {
      this.snackbarService.error(this.isDowngrade(plan)
        ? 'Não foi possível agendar a mudança de plano. Tente novamente.'
        : 'Não foi possível iniciar o checkout. Tente novamente.');
    } finally {
      this.loadingPlan = null;
    }
  }

  isLoading(planId: string): boolean {
    return this.loadingPlan === planId;
  }

  selectBillingPeriod(period: BillingSubscriptionPeriod): void {
    if (!this.loadingPlan) {
      this.billingPeriod = period;
    }
  }

  isCurrentPlan(plan: BillingPlanCatalogItem): boolean {
    if (!this.mySubscription?.planName) {
      return false;
    }
    return this.mySubscription.planName.toLowerCase() === plan.name?.toLowerCase()
      && this.getCurrentBillingPeriod() === this.billingPeriod;
  }

  formatCurrency(value: number | null | undefined): string {
    const amount = value ?? 0;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
  }

  getPlanPrice(plan: BillingPlanCatalogItem): number {
    if (this.billingPeriod === 'MONTHLY') {
      return plan.price || 0;
    }

    if (typeof plan.annualPrice === 'number') {
      return plan.annualPrice;
    }

    return (plan.price || 0) * (12 - (plan.annualDiscountMonths || 0));
  }

  getOriginalPlanPrice(plan: BillingPlanCatalogItem): number | null {
    if (this.billingPeriod === 'MONTHLY') {
      return typeof plan.originalPrice === 'number' ? plan.originalPrice : null;
    }
    return typeof plan.originalAnnualPrice === 'number' ? plan.originalAnnualPrice : null;
  }

  hasCouponDiscount(plan: BillingPlanCatalogItem): boolean {
    const originalPrice = this.getOriginalPlanPrice(plan);
    return !this.isFreePlan(plan) && originalPrice !== null && originalPrice > this.getPlanPrice(plan);
  }

  getCouponLabel(plan: BillingPlanCatalogItem): string | null {
    if (!this.hasCouponDiscount(plan) || !plan.couponDiscountPercentage) {
      return null;
    }
    return `${plan.couponDiscountPercentage}% de desconto`;
  }

  getPlanPeriodLabel(plan: BillingPlanCatalogItem): string {
    if (this.isFreePlan(plan)) {
      return 'sem cobrança';
    }
    if ((plan.trialDurationMonths || 0) > 0) {
      const period = this.billingPeriod === 'ANNUAL' ? 'ano' : 'mês';
      return `gratuíto por ${plan.trialDurationMonths} mês`;
    }

    return this.billingPeriod === 'ANNUAL' ? 'por ano' : 'por mês';
  }

  getAnnualDiscountLabel(plan: BillingPlanCatalogItem): string | null {
    if (this.billingPeriod !== 'ANNUAL' || !plan.annualDiscountMonths) {
      return null;
    }

    const unit = plan.annualDiscountMonths === 1 ? 'mês' : 'meses';
    return `Economize ${plan.annualDiscountMonths} ${unit}`;
  }

  getPlanButtonLabel(plan: BillingPlanCatalogItem): string {
    if (this.isCurrentPlan(plan) && !this.isInitialTrial()) {
      return 'Plano atual';
    }
    if (this.isCurrentPlan(plan) && this.isInitialTrial()) {
      return 'Assinar';
    }
    if (this.isLoading(plan.planId)) {
      return 'Processando...';
    }
    if (this.isTrialChangeBlocked()) {
      return 'Alterações indisponíveis';
    }
    if (this.isFreePlan(plan) && this.hasNoSubscription()) {
      return 'Indisponível sem assinatura';
    }
    return this.isDowngrade(plan) || this.isFreePlan(plan) ? 'Realizar mudança' : 'Assinar';
  }

  canCheckout(plan: BillingPlanCatalogItem): boolean {
    return !this.loadingPlan && (!this.isCurrentPlan(plan) || this.isInitialTrial()) && !this.isTrialChangeBlocked()
      && !(this.isFreePlan(plan) && this.hasNoSubscription());
  }

  isDowngrade(plan: BillingPlanCatalogItem): boolean {
    if (!this.mySubscription?.planName || this.isInitialTrial() || this.billingPeriod !== this.getCurrentBillingPeriod()) {
      return false;
    }

    const currentPlanPrice = this.getCurrentPlanPrice();
    return currentPlanPrice !== null && typeof plan?.price === 'number' && plan.price < currentPlanPrice;
  }

  hasNoSubscription(): boolean {
    return !this.mySubscription?.planName;
  }

  isFreePlan(plan: BillingPlanCatalogItem): boolean {
    return plan?.planType === 'FREE';
  }

  showRegularizeAction(): boolean {
    if (!this.mySubscription) {
      return false;
    }

    if (this.mySubscription.writeAllowed === false) {
      return true;
    }

    const status = (this.mySubscription.subscriptionStatus || '').toUpperCase();
    return ['PAST_DUE', 'CANCELED', 'CANCELLED', 'EXPIRED', 'UNPAID'].includes(status);
  }

  showPendingCheckoutAction(): boolean {
    if (!this.mySubscription?.pendingCheckoutUrl) {
      return false;
    }

    if (this.isAbandonedAuthorization()) {
      return false;
    }

    return this.mySubscription.pendingPlanChangeType === 'UPGRADE' || this.showRegularizeAction();
  }

  pendingCheckoutActionLabel(): string {
    if (this.mySubscription?.pendingPlanChangeType === 'UPGRADE') {
      return 'Finalizar upgrade';
    }
    return this.isPendingAuthorization() ? 'Retomar autorização' : 'Regularizar assinatura';
  }

  canCancelSubscription(): boolean {
    const status = (this.mySubscription?.subscriptionStatus || '').toUpperCase();
    return this.mySubscription?.canCancelRenewal === true
      && !this.loadingPlan
      && !this.isCancellationScheduled()
      && !['CANCELED', 'CANCELLED'].includes(status);
  }

  confirmCancellation(): void {
    if (!this.canCancelSubscription()) {
      return;
    }

    const data: ConfirmationDialogData = {
      dialogTitle: 'Cancelar renovação?',
      dialogMessage: 'Seu acesso continuará ativo até o fim do ciclo vigente. Ao confirmar, a próxima renovação não será cobrada.',
      confirmButtonName: 'Sim, cancelar renovação',
      cancelButtonName: 'Manter renovação',
      destructive: true
    };

    this.confirmationDialogService.openConfirmationDialog(data).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        void this.cancelSubscription();
      }
    });
  }

  async cancelSubscription(): Promise<void> {
    if (!this.canCancelSubscription()) {
      return;
    }
    this.loadingPlan = 'cancel';
    try {
      const response = await this.mySubscriptionBillingService.cancelSubscription();
      if (this.mySubscription) {
        this.mySubscription = {
          ...this.mySubscription,
          pendingPlanChangeType: 'CANCELLATION',
          pendingPlanChangeStatus: response?.status || 'PENDING'
        };
      }
      this.snackbarService.success('Renovação cancelada.');
    } catch (_error) {
      this.snackbarService.error('Não foi possível cancelar a renovação. Tente novamente.');
    } finally {
      this.loadingPlan = null;
    }
  }

  openPendingCheckout(): void {
    if (this.mySubscription?.pendingCheckoutUrl) {
      window.location.assign(this.mySubscription.pendingCheckoutUrl);
    }
  }

  private isPendingAuthorization(): boolean {
    const subscriptionStatus = (this.mySubscription?.subscriptionStatus || '').toUpperCase();
    const checkoutStatus = (this.mySubscription?.pendingCheckoutStatus || '').toUpperCase();
    return subscriptionStatus === 'PENDING' || checkoutStatus === 'PENDING';
  }

  private isAbandonedAuthorization(): boolean {
    const changeStatus = (this.mySubscription?.pendingPlanChangeStatus || '').toUpperCase();
    return changeStatus === 'AUTHORIZATION_PENDING'
      || (!this.mySubscription?.pendingPlanChangeType && this.isPendingAuthorization());
  }

  isCancellationScheduled(): boolean {
    return this.mySubscription?.pendingPlanChangeType === 'CANCELLATION';
  }

  getCurrentPlanBadgeLabel(): string {
    if (this.isCancellationScheduled()) {
      const endDate = this.formatDate(this.mySubscription?.currentPeriodEnd);
      return endDate ? `Vigente até ${endDate}` : 'Renovação cancelada';
    }

    if (this.isTrialExpired()) {
      return 'Teste vencido';
    }

    if (this.isTrial()) {
      const daysRemaining = this.mySubscription?.trialDaysRemaining;
      if (daysRemaining === 0) {
        return 'Em teste — termina hoje';
      }
      if (typeof daysRemaining === 'number') {
        const unit = daysRemaining === 1 ? 'dia' : 'dias';
        return `Em teste — faltam ${daysRemaining} ${unit}`;
      }
      return 'Período de teste';
    }

    return 'Assinatura atual';
  }

  private isTrialChangeBlocked(): boolean {
    return this.mySubscription?.planChangesBlocked === true;
  }

  private isInitialTrial(): boolean {
    return (this.isTrial() || this.isTrialExpired())
      && this.mySubscription?.canCancelRenewal !== true;
  }

  private isTrial(): boolean {
    return (this.mySubscription?.subscriptionStatus || '').toUpperCase() === 'TRIALING';
  }

  isTrialExpired(): boolean {
    return this.mySubscription?.trialExpired === true;
  }

  private isCurrentFreePlan(): boolean {
    return this.mySubscription?.planType === 'FREE';
  }

  getServiceQuantity(plan: BillingPlanCatalogItem, service: string): number | null {
    const serviceData = plan.services?.find(item => this.normalizeServiceKey(item.service) === this.normalizeServiceKey(service));
    if (!serviceData) {
      return null;
    }
    return serviceData.quantity;
  }

  getCurrentLimit(service: string): BillingLimitUsage | null {
    if (!this.mySubscription?.limits?.length) {
      return null;
    }
    return this.mySubscription.limits.find(limit => this.normalizeServiceKey(limit.serviceType) === this.normalizeServiceKey(service)) || null;
  }

  getCurrentUsage(service: string): number | null {
    return this.getCurrentLimit(service)?.used ?? null;
  }

  private buildComparisonServices(plans: BillingPlanCatalogItem[], mySubscription: BillingMySubscriptionSummary | null): string[] {
    const services = new Set<string>();
    const fixedServiceKeys = new Set(
      this.fixedComparisonItems
        .filter(item => !!item.serviceKey)
        .map(item => this.normalizeServiceKey(item.serviceKey || ''))
    );

    plans.forEach(plan => {
      plan.services?.forEach(serviceItem => {
        if (serviceItem.service && !fixedServiceKeys.has(this.normalizeServiceKey(serviceItem.service))) {
          services.add(serviceItem.service);
        }
      });
    });
    mySubscription?.limits?.forEach(limit => {
      if (limit.serviceType && !fixedServiceKeys.has(this.normalizeServiceKey(limit.serviceType))) {
        services.add(limit.serviceType);
      }
    });
    return Array.from(services.values());
  }

  private ensureCurrentPlanInCatalog(plans: BillingPlanCatalogItem[], mySubscription: BillingMySubscriptionSummary | null): BillingPlanCatalogItem[] {
    if (!mySubscription?.planName) {
      return plans;
    }

    const alreadyIncluded = plans.some(plan => this.normalizeServiceKey(plan.name) === this.normalizeServiceKey(mySubscription.planName || ''));
    if (alreadyIncluded) {
      return plans;
    }

    const fallbackServices = (mySubscription.limits || []).map(limit => ({
      service: limit.serviceType,
      quantity: limit.limit
    }));

    return [
      {
        planId: `current-${mySubscription.planName.toLowerCase()}`,
        name: mySubscription.planName,
        price: 0,
        planType: mySubscription.planType || undefined,
        defaultPlan: false,
        trialDurationMonths: null,
        services: fallbackServices
      },
      ...plans
    ];
  }

  private normalizeServiceKey(value: string): string {
    return (value || '').trim().toLowerCase();
  }

  private getCurrentBillingPeriod(): BillingSubscriptionPeriod {
    return this.mySubscription?.billingPeriod || 'MONTHLY';
  }

  private getCurrentPlanPrice(): number | null {
    if (typeof this.mySubscription?.currentPlanPrice === 'number') {
      return this.mySubscription.currentPlanPrice;
    }

    const currentPlan = this.catalog.find(plan => this.isCurrentPlan(plan));
    return typeof currentPlan?.price === 'number' ? currentPlan.price : null;
  }

  private getDowngradeScheduledMessage(effectiveAt: string | undefined): string {
    if (!effectiveAt) {
      return 'Mudança de plano agendada para o próximo ciclo.';
    }

    const [year, month, day] = effectiveAt.split('-').map(Number);
    if (!year || !month || !day) {
      return 'Mudança de plano agendada para o próximo ciclo.';
    }
    return `Mudança de plano agendada para ${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}.`;
  }

  private formatDate(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
  }

  private async loadMySubscription(): Promise<BillingMySubscriptionSummary | null> {
    try {
      return await this.mySubscriptionBillingService.getMySubscription();
    } catch (error: any) {
      if (error?.status === 204 || error?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  private handleCheckoutStatus(): void {
    let checkoutStatus =
      this.route.snapshot.queryParamMap.get('checkoutStatus') ??
      this.route.snapshot.queryParamMap.get('status') ??
      this.route.snapshot.queryParamMap.get('collection_status');
    if (!checkoutStatus) {
      return;
    }
    if (checkoutStatus === 'approved') {
      checkoutStatus = 'success';
    } else if (checkoutStatus === 'rejected') {
      checkoutStatus = 'failure';
    }

    if (checkoutStatus === 'success') {
      this.snackbarService.success('Autorização concluída. Estamos confirmando a assinatura.');
      return;
    }

    if (checkoutStatus === 'pending') {
      this.snackbarService.warning('Checkout pendente. Finalize o pagamento para ativar o plano.');
      return;
    }

    if (checkoutStatus === 'cancel' || checkoutStatus === 'canceled') {
      this.snackbarService.warning('Checkout cancelado.');
      return;
    }

    if (checkoutStatus === 'error' || checkoutStatus === 'failure') {
      this.snackbarService.error('Não foi possível concluir o checkout.');
    }
  }

  private buildReturnUrl(status: string): string {
    return `${window.location.origin}/#/plan?checkoutStatus=${status}`;
  }
}
